import { CurveBounds, EventArrowLayer, EventMarkerLayer } from '@statsbomb/kitbag-datavis';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { unwrappedMarkersDescriptionsAtom } from '@/atoms/eventMarkers';
import { eventLinkAtom } from '@/atoms/navigation';
import { selectVisEventsAtom, selectedVisEventsAtom } from '@/atoms/vis/selection';
import { showMarkerStartAtom } from '@/atoms/visualisation';
import { PITCH_HEIGHT, PITCH_WIDTH } from '@/consts/HeatMap';
import {
  DEFAULT_MARKER_SHAPE,
  GOAL_OUTCOME,
  SAVED_SHOT_OUTCOMES,
  SHOT_MAP_MARKER_STROKE_WIDTH,
  SHOT_MARKER_FILL_OPACITY,
} from '@/consts/visualisations';
import { useEventMapColouring } from '@/hooks/eventMap/useEventMapColouring';
import useGetLinkPath from '@/hooks/useGetLinkPath';
import { useGetPitchRotation } from '@/hooks/useGetPitchRotation';
import { useResetSelectionLayer } from '@/hooks/useResetSelectionLayer';
import { ArrowEvent, MarkerEvent } from '@/types/event';
import { ShotMapEventMarker } from '@/types/visualisation';
import { isNullish } from '@/utils/predicates';
import { getShotMarkerShape, isShotEvent } from '@/utils/visualisations';
import { EventLayerTooltip } from './EventLayerTooltip';

const PITCH_BOUNDS: CurveBounds = { minX: 0, minY: 0, maxX: PITCH_WIDTH, maxY: PITCH_HEIGHT };

export const EventMapLayers = ({
  eventMapLayersRef,
  currentlySelectedVideoId,
  allowInteraction,
  markerEvents,
  arrowEvents,
  useDefaultColor,
}: {
  eventMapLayersRef?: React.Ref<SVGGElement>;
  currentlySelectedVideoId: string | null;
  allowInteraction?: boolean;
  markerEvents: MarkerEvent[];
  arrowEvents: ArrowEvent[];
  useDefaultColor?: boolean;
}) => {
  const selectedVisEvents = useAtomValue(selectedVisEventsAtom);
  const selectVisEvents = useSetAtom(selectVisEventsAtom);
  const markersDescriptions = useAtomValue(unwrappedMarkersDescriptionsAtom);

  const allEvents = [...markerEvents, ...arrowEvents];
  const resetSelectionLayer = useResetSelectionLayer();
  const { to, relative } = useGetLinkPath(eventLinkAtom);
  const navigate = useNavigate();

  const showMarkerStart = useAtomValue(showMarkerStartAtom);

  const { getShotColours, getEventColour } = useEventMapColouring(currentlySelectedVideoId, useDefaultColor);
  const pitchRotation = useGetPitchRotation(false);
  const shotRotation = pitchRotation === 'VERTICAL' || pitchRotation === 'VERTICAL_REVERSE' ? 90 : undefined;

  const nowPlayingEventId = allEvents.find(
    ({ videoEventId, eventId }) => (videoEventId || eventId) === currentlySelectedVideoId,
  )?.eventId;
  const nowPlayingEventIds = nowPlayingEventId ? [nowPlayingEventId] : [];

  const onMarkerClick: React.ComponentProps<typeof EventMarkerLayer>['onMarkerClick'] = allowInteraction
    ? (_, id) => {
        resetSelectionLayer();
        selectVisEvents(
          allEvents.filter(({ eventId }) => eventId === id),
          markersDescriptions.filter(({ eventId }) => eventId === id),
        );
        navigate(to, { relative });
      }
    : undefined;

  const selectedItemIds = selectedVisEvents?.map(({ eventId }) => eventId);

  return (
    <g ref={eventMapLayersRef}>
      <EventMarkerLayer
        onMarkerClick={onMarkerClick}
        markers={markerEvents?.map(marker => {
          const { eventId: id, startX, startY, endX, endY, xg, obv, gkSaveDifficultyXg } = marker;
          const x = showMarkerStart || isNullish(endX) ? startX : endX;
          const y = showMarkerStart || isNullish(endY) ? startY : endY;
          const tooltipObv = isShotEvent(marker) ? undefined : obv;
          const tooltip = allowInteraction ? (
            <EventLayerTooltip eventId={id} obv={tooltipObv} xg={xg} gkSaveDifficultyXg={gkSaveDifficultyXg} />
          ) : null;

          if (isShotEvent(marker)) {
            const shotMarker: ShotMapEventMarker = marker;
            const { fill, stroke } = getShotColours(marker);
            const isSaved = !!marker.outcome && SAVED_SHOT_OUTCOMES.includes(marker.outcome);
            return {
              id,
              x,
              y,
              shapeName: getShotMarkerShape(shotMarker),
              fill,
              fillOpacity: SHOT_MARKER_FILL_OPACITY,
              stroke,
              strokeWidth: isSaved ? SHOT_MAP_MARKER_STROKE_WIDTH.SAVED : SHOT_MAP_MARKER_STROKE_WIDTH.DEFAULT,
              doubleBorder: marker.outcome === GOAL_OUTCOME,
              rotation: shotRotation,
              tooltip,
            };
          }

          return {
            id,
            x,
            y,
            fill: getEventColour(marker),
            shapeName: DEFAULT_MARKER_SHAPE,
            tooltip,
          };
        })}
        selectedItemIds={selectedItemIds}
        highlightedItemIds={nowPlayingEventIds}
        isHighlightNowPlaying={!!nowPlayingEventId}
      />
      <EventArrowLayer
        onMarkerClick={onMarkerClick}
        arrows={arrowEvents?.map(arrow => {
          const { eventId: id, startX, startY, endX, endY, xg, obv, gkSaveDifficultyXg, curveDirection } = arrow;
          const tooltipObv = isShotEvent(arrow) ? undefined : obv;
          return {
            id,
            startX,
            startY,
            endX,
            endY,
            stroke: getEventColour(arrow),
            arrowHeadName: 'EQUILATERAL',
            curveDirection,
            bounds: PITCH_BOUNDS,
            tooltip: allowInteraction ? (
              <EventLayerTooltip eventId={id} obv={tooltipObv} xg={xg} gkSaveDifficultyXg={gkSaveDifficultyXg} />
            ) : null,
          };
        })}
        selectedItemIds={selectedItemIds}
        highlightedItemIds={nowPlayingEventIds}
        isHighlightNowPlaying={!!nowPlayingEventId}
      />
    </g>
  );
};
